(function () {
  // Desktop breakpoint ($xl): the navigation panel is desktop-only.
  const DESKTOP_MIN = 1200;
  const isDesktop = () => window.innerWidth >= DESKTOP_MIN;

  // Keep the scroll-lock through the fade-out: releasing it synchronously restores
  // the section's transform containing block mid-fade and collapses the fixed
  // panel. Release 360ms after close, only if nothing reopened.
  let desktopLockTimer = null;
  const releaseDesktopLock = () => {
    clearTimeout(desktopLockTimer);
    desktopLockTimer = setTimeout(() => {
      if (document.body.classList.contains("header-panel-open")) return;
      document.body.classList.remove("overflow-hidden-desktop-menu");
    }, 360);
  };
  window.HeaderMegaModals = window.HeaderMegaModals || {};
  window.HeaderMegaModals.releaseDesktopLock = releaseDesktopLock;

  // .shopify-section-header.animate transition (snippets/header-styles-common.liquid).
  const HEADER_SLIDE_MS = 500;
  // How long the lock keeps owning the header transform after a close
  // (releaseDesktopLock), i.e. the earliest the header can move again.
  const HEADER_RESTORE_DELAY = 360;
  // --duration-default: the panel slide + backdrop dim.
  const PANEL_TRANSITION_MS = 300;

  // The panel and its backdrop are fixed inside .shopify-section-header, so the lock
  // class has to drop that section's transform — which also means the header cannot
  // move while a panel is open. An open fired from a scrolled page (sticky add to
  // cart, quick view) is therefore two steps: slide the hidden header back in, THEN
  // hand the viewport over to the panel; the close plays it back so the header ends
  // up in the state it was in before.
  let headerWasHidden = false;
  let headerSlideTimer = null;

  const stickyHeaderSection = () =>
    document.querySelector(".shopify-section-header");

  const setStickyHeaderPreventHide = (prevent) => {
    const wrapper = document.querySelector(".header-wrapper");
    if (wrapper) wrapper.preventHide = prevent;
  };

  // Calls onSettled once the header is at rest: synchronously when it is already on
  // screen, after the slide when it had to come back. preventHide holds it there —
  // the page is still scrollable until the panel commits its lock.
  const revealStickyHeader = (onSettled) => {
    clearTimeout(headerSlideTimer);
    const header = stickyHeaderSection();
    headerWasHidden = Boolean(
      header?.classList.contains("shopify-section-header-hidden")
    );
    setStickyHeaderPreventHide(true);

    if (!headerWasHidden) {
      onSettled();
      return;
    }

    header.classList.add("animate");
    header.classList.remove("shopify-section-header-hidden");
    headerSlideTimer = setTimeout(onSettled, HEADER_SLIDE_MS);
  };

  // Only a header that was hidden when the panel took over slides away again; one the
  // visitor had on screen stays.
  const restoreStickyHeader = () => {
    setStickyHeaderPreventHide(false);
    if (!headerWasHidden) return;
    headerWasHidden = false;
    // a reopen inside the restore delay keeps the header where the panel needs it
    if (document.body.classList.contains("header-panel-open")) return;
    stickyHeaderSection()?.classList.add(
      "animate",
      "shopify-section-header-hidden"
    );
  };

  const scheduleStickyHeaderRestore = () => {
    clearTimeout(headerSlideTimer);
    headerSlideTimer = setTimeout(restoreStickyHeader, HEADER_RESTORE_DELAY);
  };

  // An explicitly opened cart panel (click, Enter, add to cart) is a dialog, so Tab
  // must not walk out of it into the page behind. Only the Tab boundaries are
  // hijacked; a hover or Tab-focus open stays a preview the visitor tabs through.
  let cartTabHandler = null;

  const releaseCartTabTrap = () => {
    if (!cartTabHandler) return;
    document.removeEventListener("keydown", cartTabHandler, true);
    cartTabHandler = null;
  };

  const setCartTabTrap = (content) => {
    releaseCartTabTrap();
    cartTabHandler = (event) => {
      if (event.code.toUpperCase() !== "TAB") return;
      if (!content.contains(event.target)) return;

      // Recomputed on every Tab: a cart mutation replaces the drawer markup and
      // the recommendations arrive after the panel is already open.
      const elements = getFocusableElements(content);
      if (!elements.length) return;

      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey ? event.target === first : event.target === last) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    };
    document.addEventListener("keydown", cartTabHandler, true);
  };

  // The header dropdowns (account) and the desktop navigation panel are mutually
  // exclusive — the other direction lives in header-dropdown.openDropdown.
  const closeHeaderDropdowns = () => {
    document.querySelectorAll("header-dropdown[open]").forEach((dropdown) => {
      dropdown.closeForHandoff?.();
    });
  };

  // Idempotent header re-init (theme editor): clear the pending timer, open-state
  // body classes and stale closures before wiring the new DOM.
  const resetHeaderModalState = () => {
    clearTimeout(desktopLockTimer);
    clearTimeout(headerSlideTimer);
    headerWasHidden = false;
    setStickyHeaderPreventHide(false);
    releaseCartTabTrap();
    document.body.classList.remove(
      "overflow-hidden-desktop-menu",
      "header-panel-open",
      "header-panel-open--menu",
      "header-panel-open--icons",
      "overflow-hidden-search-open"
    );
    // panel visibility is class-driven (no body-class dependency) — clear any
    // leftover open state on re-init
    document
      .querySelectorAll(
        ".header-desktop-navigation-panel--active, .header-nav-panel__content--active"
      )
      .forEach((el) => {
        el.classList.remove(
          "header-desktop-navigation-panel--active",
          "header-nav-panel__content--active"
        );
      });
    const mega = (window.HeaderMegaModals = window.HeaderMegaModals || {});
    const noop = () => {};
    mega.closePanel = noop;
    mega.openPanel = noop;
    // Preserved API names (theme-editor.js + sibling modals depend on them):
    mega.closeMegaMenu = noop;
    mega.closeMegaSubmenu = noop;
    mega.closeMegaCards = noop;
    mega.closeSearchModal = noop;
    mega.openMegaMenu = noop;
    mega.openMegaSubmenu = noop;
    mega.openMegaCards = noop;
  };

  // True when the fired event concerns the HEADER (other sections' loads must not
  // reset an open header panel).
  const isHeaderSectionEvent = (event) => {
    if (!event) return true;
    const target = event.target;
    if (!target) return true;
    if (
      target.classList &&
      target.classList.contains("shopify-section-header")
    ) {
      return true;
    }
    return Boolean(
      target.querySelector && target.querySelector(".header-wrapper")
    );
  };

  // Overlay-mode margin + breadcrumb colour (first section slides under a
  // transparent header).
  const initHeaderOverlay = (header) => {
    const main = document.getElementById("MainContent");
    if (!main) return;
    const sections = main.querySelectorAll(".shopify-section");
    const breadcrumbs = document.querySelector("#breadcrumbs");
    const hasOverlayMode = Boolean(
      header.querySelector(".header-wrapper--overlay-mode")
    );

    const removeColorScheme = (element) => {
      if (!element) return;
      let classNames = element.getAttribute("class");
      classNames = classNames.replace(/color-background-\d+/g, "");
      element.setAttribute("class", classNames);
    };

    if (sections.length > 0 && hasOverlayMode) {
      const hasTargetSection =
        sections[0].classList.contains("has-header-overlay");
      const sectionTargetChild = sections[0].querySelector(
        "[data-header-overlay]"
      );
      const headerGroupSections = document.querySelectorAll(
        ".shopify-section-group-header-group"
      );

      if (hasTargetSection && sectionTargetChild) {
        if (headerGroupSections[headerGroupSections.length - 1] === header) {
          sections[0].classList.remove("not-margin");
          header.classList.remove("not-mode");
          if (breadcrumbs) {
            removeColorScheme(breadcrumbs);
            const colorScheme = sectionTargetChild.dataset.colorScheme;
            if (colorScheme) breadcrumbs.classList.add(`color-${colorScheme}`);
          }
        } else {
          sections[0].classList.add("not-margin");
          header.classList.add("not-mode");
          if (breadcrumbs) removeColorScheme(breadcrumbs);
        }
      } else {
        header.classList.add("not-mode");
        if (breadcrumbs) removeColorScheme(breadcrumbs);
      }
    } else {
      header.classList.add("not-mode");
      if (breadcrumbs) removeColorScheme(breadcrumbs);
    }
  };

  // Top-level item geometry: every item gets --distance-to-menu (item bottom →
  // header bottom). Native inline submenus anchor their dropdown with it; the
  // open mega trigger sizes its hover bridge down to the navigation panel
  // (a wrapped long menu would otherwise close the panel while the pointer
  // travels across the rows below).
  const initDesktopSubmenu = (header) => {
    const headerBox = header.querySelector(".header-inner");
    if (!headerBox) return;

    const menuItems = headerBox.querySelectorAll(".header-inline-menu__item");
    if (!menuItems.length) return;

    const calcSubmenuPosition = () => {
      const headerRect = headerBox.getBoundingClientRect();
      menuItems.forEach((itemEl) => {
        const itemRect = itemEl.getBoundingClientRect();
        const delta = headerRect.bottom - itemRect.bottom + 10;
        const distanceToMenu = Math.ceil(delta / 10) * 10;
        itemEl.style.setProperty("--distance-to-menu", `${distanceToMenu}px`);
        const submenuEl = itemEl.querySelector(".header-inline-submenu");
        if (submenuEl) submenuEl.style.top = `calc(100% + ${delta}px)`;
      });
    };

    if (header._submenuObserver) header._submenuObserver.disconnect();
    const observer = new ResizeObserver(() => calcSubmenuPosition());
    observer.observe(headerBox);
    header._submenuObserver = observer;

    headerBox
      .querySelectorAll(".header-inline-menu__item--submenu")
      .forEach((itemEl) => {
        if (itemEl.dataset.submenuInit === "true") return;
        itemEl.dataset.submenuInit = "true";
        const triggerEl = itemEl.querySelector(".header-inline-menu__link");

        const setDismissed = (dismissed) => {
          itemEl.classList.toggle(
            "header-inline-menu__item--dismissed",
            dismissed
          );
          triggerEl?.setAttribute("aria-expanded", String(!dismissed));
        };
        triggerEl?.setAttribute("aria-expanded", "false");

        itemEl.addEventListener("mouseenter", () => setDismissed(false));
        itemEl.addEventListener("focusin", () => {
          if (
            !itemEl.classList.contains("header-inline-menu__item--dismissed")
          ) {
            triggerEl?.setAttribute("aria-expanded", "true");
          }
        });
        itemEl.addEventListener("focusout", (event) => {
          if (itemEl.contains(event.relatedTarget)) return;
          itemEl.classList.remove("header-inline-menu__item--dismissed");
          triggerEl?.setAttribute("aria-expanded", "false");
        });

        itemEl.addEventListener("keydown", (event) => {
          // a dismissed submenu reopens from its <div role="button"> trigger
          if (
            (event.key === "Enter" || event.key === " ") &&
            event.target === triggerEl &&
            triggerEl.getAttribute("role") === "button"
          ) {
            event.preventDefault();
            setDismissed(false);
            return;
          }

          if (event.key !== "Escape") return;
          // an open third level closes first, back onto its own summary
          const openDetails = event.target.closest(
            ".header-inline-submenu__details[open]"
          );
          if (openDetails) {
            openDetails.removeAttribute("open");
            const summary = openDetails.querySelector("summary");
            (summary?.querySelector(".js-submenu-toggle") || summary)?.focus();
            return;
          }
          setDismissed(true);
          triggerEl?.focus();
        });

        itemEl.addEventListener("mouseleave", () => {
          const focusElement = document.activeElement;
          if (itemEl.contains(focusElement)) focusElement.blur();
          triggerEl?.setAttribute("aria-expanded", "false");
        });
      });
  };

  // The dropdown hangs off the icon row, which is centred in the taller header
  // row: without this it opens level with the icons and covers the header edge.
  const initDropdownOffset = (header) => {
    const headerBox = header.querySelector(".header-inner");
    const dropdowns = header.querySelectorAll(".header-dropdown__modal");
    if (!headerBox || !dropdowns.length) return;

    const apply = () => {
      const headerBottom = headerBox.getBoundingClientRect().bottom;
      dropdowns.forEach((modal) => {
        const anchor = modal.offsetParent || modal.parentElement;
        if (!anchor) return;
        const drop = headerBottom - anchor.getBoundingClientRect().bottom + 10;
        modal.style.setProperty(
          "--header-dropdown-offset",
          `${Math.max(Math.round(drop), 10)}px`
        );
      });
    };
    apply();

    if (header._dropdownObserver) header._dropdownObserver.disconnect();
    const observer = new ResizeObserver(apply);
    observer.observe(headerBox);
    header._dropdownObserver = observer;
  };

  // A centred logo leaves the flow, so a large one runs under the burger on one
  // side and the icon row on the other. Cap it to the free space between them.
  const initCentredLogoWidth = (header) => {
    const inner = header.querySelector(".header-inner");
    if (!inner) return;

    const apply = () => {
      const logo = inner.querySelector(
        ".header-inner__center-logo, .header-inner--mobile-logo-center .header-inner__logo .header-logo:not(.header-logo--in-drawer)"
      );
      if (!logo || getComputedStyle(logo).position !== "absolute") {
        inner.style.removeProperty("--header-centred-logo-max");
        return;
      }

      const box = inner.getBoundingClientRect();
      const centre = box.left + box.width / 2;
      const burger = inner.querySelector(".menu-drawer__button");
      const icons = inner.querySelector(
        ".header-nav__icons, .header-nav-group--end"
      );
      const left = burger ? burger.getBoundingClientRect().right : box.left;
      const right = icons ? icons.getBoundingClientRect().left : box.right;
      // 1.6rem of breathing room on the tighter side, mirrored to both
      const half = Math.min(centre - left, right - centre) - 16;
      inner.style.setProperty(
        "--header-centred-logo-max",
        `${Math.max(Math.round(half * 2), 0)}px`
      );
    };
    apply();

    if (header._centredLogoObserver) header._centredLogoObserver.disconnect();
    const observer = new ResizeObserver(apply);
    observer.observe(inner);
    header._centredLogoObserver = observer;
  };

  // logo-left-menu-left only: keep the --menu panel at least as wide as the menu
  // group (+3.2rem). The group is content-driven, so measure it live and feed
  // --header-menu-group-width; other layouts leave it at 0 (no effect).
  const initMenuGroupWidth = (header) => {
    if (header._menuGroupObserver) {
      header._menuGroupObserver.disconnect();
      header._menuGroupObserver = null;
    }

    const group = header.querySelector(
      ".header-inner--layout-logo-left-menu-left .header-nav-group--start, .header-inner--layout-logo-center-menu-left .header-nav-group--start"
    );
    if (!group) {
      header.style.removeProperty("--header-menu-group-width");
      return;
    }

    const apply = () => {
      // scrollWidth as well: a label that cannot wrap overflows the group box,
      // and the panel has to cover what is actually painted.
      const width = Math.max(
        group.getBoundingClientRect().width,
        group.scrollWidth
      );
      header.style.setProperty("--header-menu-group-width", `${width}px`);
    };
    apply();

    const observer = new ResizeObserver(apply);
    observer.observe(group);
    header._menuGroupObserver = observer;
  };

  // Mobile drawer mega (submenu / menu / cards share the mobile drawer).
  const initMobileMegaSubmenu = (header) => {
    const megaSubmenuLinks = header.querySelectorAll(
      "header-mobile-drawer .header-mobile-menu__item--megasubmenu, header-mobile-drawer .header-mobile-menu__item--megamenu, header-mobile-drawer .header-mobile-menu__item--megacards"
    );
    if (!megaSubmenuLinks || !megaSubmenuLinks.length) return;

    const openMobileMegaSubmenu = (megaSubmenuEl, parentLink) => {
      if (
        !megaSubmenuEl ||
        !megaSubmenuEl.classList.contains("mega-submenu-mobile")
      ) {
        return;
      }
      megaSubmenuEl.classList.add("mega-submenu-mobile--visible");
      parentLink.setAttribute("aria-expanded", "true");

      // The panel is still visibility: hidden on the frame it opens, so focus
      // would bounce off it; move in once the slide-in has made it visible.
      const wrapper = megaSubmenuEl.querySelector(
        ".mega-submenu-mobile__wrapper"
      );
      let focusEntered = false;
      const enterFocus = () => {
        if (focusEntered) return;
        focusEntered = true;
        clearTimeout(megaSubmenuEl._focusTimer);
        if (megaSubmenuEl.classList.contains("mega-submenu-mobile--visible")) {
          trapFocus(wrapper);
        }
      };
      megaSubmenuEl.addEventListener("transitionend", enterFocus, {
        once: true,
      });
      clearTimeout(megaSubmenuEl._focusTimer);
      megaSubmenuEl._focusTimer = setTimeout(enterFocus, 350);
    };

    const closeMobileMegaSubmenu = (megaSubmenuEl, parentLink) => {
      if (
        !megaSubmenuEl ||
        !megaSubmenuEl.classList.contains("mega-submenu-mobile")
      ) {
        return;
      }
      clearTimeout(megaSubmenuEl._focusTimer);
      megaSubmenuEl.classList.remove("mega-submenu-mobile--visible");
      parentLink.setAttribute("aria-expanded", "false");
      // back into the drawer's own trap, not out of every trap
      const drawer = parentLink.closest(".menu-drawer-container[open]");
      drawer ? trapFocus(drawer, parentLink) : removeTrapFocus(parentLink);
    };

    megaSubmenuLinks.forEach((item) => {
      // the caret button of a link row, or the whole label otherwise
      const triggerEl = item.querySelector(".js-mega-trigger");
      const rowEl = item.querySelector(".js-mega-trigger-row");
      const megaSubmenuEl = item.querySelector(".mega-submenu-mobile");
      if (!triggerEl || !megaSubmenuEl) return;
      if (item.dataset.megaMobileInit === "true") return;
      item.dataset.megaMobileInit = "true";

      const closeButton = megaSubmenuEl.querySelector(
        ".js-mega-submenu-mobile-close-trigger"
      );

      triggerEl.setAttribute("aria-expanded", "false");
      triggerEl.setAttribute("aria-haspopup", "dialog");

      if (rowEl) {
        // The label link navigates; anywhere else on the row opens the panel.
        rowEl.addEventListener("click", (event) => {
          if (event.target.closest("a[href]")) return;
          event.preventDefault();
          openMobileMegaSubmenu(megaSubmenuEl, triggerEl);
        });
      } else {
        triggerEl.addEventListener("click", (event) => {
          event.preventDefault();
          openMobileMegaSubmenu(megaSubmenuEl, triggerEl);
        });

        // a <span role="button"> gets no native keyboard activation
        triggerEl.addEventListener("keydown", (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          openMobileMegaSubmenu(megaSubmenuEl, triggerEl);
        });
      }

      closeButton?.addEventListener("click", (event) => {
        event.preventDefault();
        closeMobileMegaSubmenu(megaSubmenuEl, triggerEl);
      });

      megaSubmenuEl.classList.add("mega-submenu-mobile--initialized");
    });
  };

  // Desktop navigation panel(s) (>= 1200px): a bound trigger carries
  // data-panel-target, each content block data-panel-content; hover/focus/click
  // swaps the visible content. Default = ONE panel, menu-left = TWO (menu → left,
  // search → right). Only the panel holding the active content shows (CSS :has).
  const initDesktopPanel = (header) => {
    const panels = header.querySelectorAll("[data-header-panel]");
    if (!panels.length) return;

    const backdrop = header.querySelector("[data-header-panel-backdrop]");
    // contents span every panel instance
    const contents = header.querySelectorAll("[data-panel-content]");
    const triggers = header.querySelectorAll("[data-panel-target]");

    let openKey = null;

    const firstFocusable = (el) =>
      el.matches("a[href], button, [tabindex]:not([tabindex^='-'])")
        ? el
        : getFocusableElements(el)[0] || null;

    const syncTriggerExpanded = (key) => {
      triggers.forEach((t) =>
        firstFocusable(t)?.setAttribute(
          "aria-expanded",
          String(key !== null && t.dataset.panelTarget === key)
        )
      );
    };

    // Triggers open on focusin, so handing focus back to one on close must not
    // reopen the panel it just left.
    let suppressFocusOpen = false;
    const focusTriggerQuietly = (trigger) => {
      suppressFocusOpen = true;
      firstFocusable(trigger)?.focus();
      suppressFocusOpen = false;
    };

    const activateTab = (content, tab) => {
      const id = tab.dataset.megaTab;
      content.querySelectorAll(".mega-menu__tab").forEach((t) => {
        t.classList.toggle("mega-menu__tab--active", t === tab);
        t.classList.toggle("hover-arrow-trigger--active", t === tab);
      });
      content
        .querySelectorAll(".mega-menu__submenu")
        .forEach((s) =>
          s.classList.toggle(
            "mega-menu__submenu--active",
            s.dataset.megaPanel === id
          )
        );
      const itemPromo = content.querySelector(
        `.mega-menu__card[data-mega-promo="${id}"]`
      );
      content
        .querySelectorAll(".mega-menu__card")
        .forEach((c) => c.classList.remove("mega-menu__card--active"));
      if (itemPromo) {
        itemPromo.classList.add("mega-menu__card--active");
      } else {
        const def = content.querySelector(
          '.mega-menu__card[data-mega-promo="default"]'
        );
        if (def) def.classList.add("mega-menu__card--active");
      }
    };

    const resetContent = (content) => {
      if (!content) return;
      // On open NO tab is active (activates on hover); the mega menu still shows
      // its default promo card. Re-opening resets to this no-active state.
      content.querySelectorAll(".mega-menu__tab").forEach((t) => {
        t.classList.remove("mega-menu__tab--active");
        t.classList.remove("hover-arrow-trigger--active");
      });
      content
        .querySelectorAll(".mega-menu__submenu")
        .forEach((s) => s.classList.remove("mega-menu__submenu--active"));
      content
        .querySelectorAll(".mega-menu__card")
        .forEach((c) => c.classList.remove("mega-menu__card--active"));
      const def = content.querySelector(
        '.mega-menu__card[data-mega-promo="default"]'
      );
      if (def) def.classList.add("mega-menu__card--active");
    };

    // Only the panel holding the active content shows: it carries the
    // --active modifier (drives visibility + the slide/fade transitions);
    // aria-hidden and the body --menu/--icons mirrors (group lowering +
    // overlay-mode colour reset) follow the same owner.
    const syncPanels = (active) => {
      const owner = active ? active.closest("[data-header-panel]") : null;
      panels.forEach((p) => {
        const isOwner = p === owner;
        p.classList.toggle("header-desktop-navigation-panel--active", isOwner);
        p.setAttribute("aria-hidden", isOwner ? "false" : "true");
      });
      document.body.classList.toggle(
        "header-panel-open--menu",
        Boolean(
          owner &&
          owner.classList.contains("header-desktop-navigation-panel--menu")
        )
      );
      document.body.classList.toggle(
        "header-panel-open--icons",
        Boolean(
          owner &&
          owner.classList.contains("header-desktop-navigation-panel--icons")
        )
      );
    };

    const showContent = (key) => {
      let active = null;
      contents.forEach((c) => {
        const isActive = c.dataset.panelContent === key;
        c.classList.toggle("header-nav-panel__content--active", isActive);
        if (isActive) active = c;
      });
      if (active) resetContent(active);
      syncPanels(active);
      return active;
    };

    let closeContentTimer = null;
    // Set while the header slides back in and the panel is waiting its turn.
    let pendingOpen = false;

    // openKey belongs to THIS init; a re-init on the same DOM (section reorder,
    // hot reload) keeps the old trigger closures bound while the shared
    // HeaderMegaModals API points here, so the body class — not openKey alone —
    // decides whether a panel is currently open.
    const isPanelOpen = () =>
      document.body.classList.contains("header-panel-open");

    // The reveal runs a frame behind the lock class: the lock drops the section
    // transform (re-parenting the fixed panel + backdrop to the viewport) and
    // mounts the backdrop, and Safari swallows a fade started in the same frame
    // its layer is built — the dim flashed in and straight back out.
    let revealFrame = null;

    const scheduleReveal = () => {
      if (revealFrame !== null || isPanelOpen()) return;
      revealFrame = requestAnimationFrame(() => {
        revealFrame = requestAnimationFrame(() => {
          revealFrame = null;
          // a close (or a theme-editor re-init, which drops the lock class)
          // between the two frames cancels the reveal
          if (
            openKey === null ||
            !document.body.classList.contains("overflow-hidden-desktop-menu")
          ) {
            return;
          }
          document.body.classList.add("header-panel-open");
        });
      });
    };

    const cancelReveal = () => {
      if (revealFrame === null) return;
      cancelAnimationFrame(revealFrame);
      revealFrame = null;
    };

    const closePanel = () => {
      if (openKey === null && !isPanelOpen() && !pendingOpen) return;
      // focus left inside a hidden panel would drop to <body>
      const activeTrigger = Array.from(triggers).find((t) =>
        t.classList.contains("header-nav-trigger--active")
      );
      const focusInPanel = Array.from(panels).some((p) =>
        p.contains(document.activeElement)
      );
      const returnTarget = panelReturnFocus;
      openKey = null;
      pendingOpen = false;
      searchFocusRequested = false;
      cartModal = false;
      panelFocusRequested = false;
      panelReturnFocus = null;
      cancelReveal();
      releaseCartTabTrap();
      syncPanels(null); // drops --active -> the panel slides back + fades out
      triggers.forEach((t) => t.classList.remove("header-nav-trigger--active"));
      syncTriggerExpanded(null);
      if (focusInPanel) {
        // the opener may be gone by now (a closed quick view) — fall back to the
        // header trigger
        returnTarget?.focus({ preventScroll: true });
        if (document.activeElement !== returnTarget && activeTrigger) {
          focusTriggerQuietly(activeTrigger);
        }
      }
      document.body.classList.remove(
        "header-panel-open",
        "overflow-hidden-search-open"
      );
      // keep the active content mounted through the exit transition, then clear
      // it so the next open re-runs the content fade/stagger animations
      clearTimeout(closeContentTimer);
      closeContentTimer = setTimeout(() => {
        if (
          openKey !== null ||
          document.body.classList.contains("header-panel-open")
        ) {
          return;
        }
        contents.forEach((c) =>
          c.classList.remove("header-nav-panel__content--active")
        );
      }, 360);
      releaseDesktopLock();
      // Queued behind that release: the lock owns the header transform until it
      // drops, so an earlier hide would snap instead of sliding.
      scheduleStickyHeaderRestore();
    };

    const contentFor = (key) =>
      Array.from(contents).find((c) => c.dataset.panelContent === key) || null;

    // Only an explicit open (click, Enter/Space, Cmd/Ctrl+K) moves focus into
    // the search field. A hover or Tab-focus open leaves focus where the user
    // put it, so Tab keeps walking the DOM order and Escape has nothing to hand
    // back to a trigger the user never used.
    let searchFocusRequested = false;

    // A cart opened from the page (add to cart, quick view) has no header trigger
    // under the visitor's focus, so focus is moved into it and handed back to the
    // control that opened it on close.
    let cartModal = false;
    let panelFocusRequested = false;
    let panelReturnFocus = null;

    const takePanelFocus = (content) => {
      if (!panelFocusRequested) return;
      panelFocusRequested = false;
      const ownerPanel = content.closest("[data-header-panel]");
      clearTimeout(ownerPanel?._panelFocusTimer);
      const focusEntry = () => {
        if (openKey !== content.dataset.panelContent) return;
        const entry = getFocusableElements(content)[0];
        entry?.focus({ preventScroll: true });
      };
      if (ownerPanel) {
        ownerPanel._panelFocusTimer = setTimeout(
          focusEntry,
          PANEL_TRANSITION_MS
        );
      } else {
        focusEntry();
      }
    };

    const takeSearchFocus = () => {
      if (!searchFocusRequested) return;
      searchFocusRequested = false;
      const input =
        contentFor("panel-search")?.querySelector("#Search-In-Panel");
      const ownerPanel = input?.closest("[data-header-panel]");
      if (!ownerPanel) return;
      clearTimeout(ownerPanel._searchFocusTimer);
      ownerPanel._searchFocusTimer = setTimeout(() => {
        if (openKey === "panel-search") input.focus();
      }, 300);
    };

    // Everything the panel does once the header is settled: mount the content, take
    // the viewport (the lock frees the fixed panel + backdrop from the header box)
    // and run the per-tab work.
    const commitOpen = (key) => {
      const active = showContent(key);
      if (!active) return;

      document.body.classList.add("overflow-hidden-desktop-menu");
      document.body.classList.toggle(
        "overflow-hidden-search-open",
        key === "panel-search"
      );
      scheduleReveal();

      if (key === "panel-search") {
        // slider was initialised while hidden (0 width) — update once visible
        const slider = active.querySelector(".js-search-recommended-slider");
        if (slider && slider.swiper) {
          requestAnimationFrame(() => slider.swiper.update());
        }
        takeSearchFocus();
      }

      if (key === "panel-cart") {
        // Recommendations are fetched lazily; their slider was measured while the
        // panel was hidden (0 width), so refresh both. The request is queued
        // behind the reveal — its response rebuilds a slider, and the backdrop
        // dim is paint-driven, so it stutters if both run in the same frames.
        document
          .querySelector("cart-drawer")
          ?.getRecommendationsProducts?.(PANEL_TRANSITION_MS);
        active
          .querySelectorAll(".js-section-card-slider")
          .forEach((sliderEl) => {
            if (sliderEl.swiper) {
              requestAnimationFrame(() => sliderEl.swiper.update());
            }
          });
        if (cartModal) {
          setCartTabTrap(active);
        } else {
          releaseCartTabTrap();
        }
        takePanelFocus(active);
      } else {
        releaseCartTabTrap();
      }
    };

    const openPanel = (
      key,
      trigger,
      { focusSearch = false, modal = false, focusPanel = false } = {}
    ) => {
      if (!isDesktop() || !key) return;
      if (!contentFor(key)) return; // no content for this key — nothing to open

      searchFocusRequested = focusSearch && key === "panel-search";

      const wasOpen = openKey === key && (isPanelOpen() || pendingOpen);
      const isCart = key === "panel-cart";
      // A hover or focus over an open cart must not downgrade the dialog.
      cartModal = isCart && (modal || (wasOpen && cartModal));
      panelFocusRequested = isCart && focusPanel;
      if (focusPanel) {
        panelReturnFocus =
          trigger instanceof HTMLElement && !header.contains(trigger)
            ? trigger
            : null;
      } else if (!wasOpen) {
        panelReturnFocus = null;
      }

      closeHeaderDropdowns();

      triggers.forEach((t) =>
        t.classList.toggle(
          "header-nav-trigger--active",
          t.dataset.panelTarget === key
        )
      );
      syncTriggerExpanded(key);

      const engaged = isPanelOpen() || pendingOpen;
      if (openKey === key && engaged) {
        // open already (a focus/hover open just ran) — only the focus is left
        if (!pendingOpen) {
          takeSearchFocus();
          const content = contentFor(key);
          if (cartModal) setCartTabTrap(content);
          takePanelFocus(content);
        }
        return;
      }
      openKey = key;

      // A tab swap (or a hover handoff) happens with the header already settled, so
      // it goes straight through; a pending open just retargets its own commit.
      if (engaged) {
        if (!pendingOpen) commitOpen(key);
        return;
      }

      pendingOpen = true;
      revealStickyHeader(() => {
        pendingOpen = false;
        if (openKey === null) return; // closed while the header slid back in
        commitOpen(openKey);
      });
    };

    // Wire triggers: menu items open on hover/focus/click; icon buttons open on
    // click/focus + hover-handoff (only if a panel is already open).
    triggers.forEach((trigger) => {
      if (trigger.dataset.panelTriggerInit === "true") return;
      trigger.dataset.panelTriggerInit = "true";
      const key = trigger.dataset.panelTarget;
      const isIconButton = trigger.classList.contains("header-button");

      const open = () => openPanel(key, trigger);

      trigger.addEventListener("focusin", () => {
        if (!suppressFocusOpen) open();
      });

      // Without a top-level link the item is a <div role="button">: focus opens
      // the panel, but after Escape only Enter/Space can bring it back.
      const roleButton = trigger.querySelector(
        ':scope > .header-inline-menu__link[role="button"]'
      );
      roleButton?.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        open();
      });
      trigger.addEventListener("click", (event) => {
        // "Enable top-level link navigation" renders the item as a real <a>:
        // swallowing the click here would cancel the navigation it asks for.
        // Icon buttons (cart / search) are links too, but always open a panel.
        const topLevelLink = isIconButton
          ? null
          : event.target.closest("a.header-inline-menu__link");
        if (isDesktop() && !topLevelLink) event.preventDefault();
        openPanel(key, trigger, { focusSearch: true, modal: true });
      });

      trigger.addEventListener("mouseenter", () => {
        if (!isDesktop()) return;
        // icon buttons swap on hover only if a panel is already open (handoff)
        if (
          isIconButton &&
          !document.body.classList.contains("header-panel-open")
        ) {
          return;
        }
        open();
      });
    });

    // collapsed until a panel opens, not unset
    syncTriggerExpanded(openKey);

    // Tab has to walk INTO the open panel. Its content is a sibling of the header
    // row, not a child of the trigger, so the native order runs along the top-level
    // items and then off into the page — the panel is never reachable by keyboard.
    // An explicitly opened cart keeps its own trap (setCartTabTrap); everything else
    // reads as a linear flow: trigger -> panel content -> close -> next control.
    const focusAfterTrigger = (trigger) => {
      const outside = getFocusableElements(document.body).filter(
        (el) => !el.closest("[data-header-panel]")
      );
      const index = outside.indexOf(firstFocusable(trigger));
      // skip what refuses focus (Shopify's hidden pixel iframes sit right after the
      // header)
      for (const el of outside.slice(index + 1)) {
        el.focus();
        if (document.activeElement === el) return;
      }
    };

    // A mega menu lists every tab before every pane, and only the focused tab's
    // pane shows, so the native order skips straight past all panes but the last.
    // Walk it as tab -> its pane links -> next tab instead.
    const megaTabStep = (content, target, backwards) => {
      const tabs = Array.from(content.querySelectorAll(".mega-menu__tab"));
      if (!tabs.length) return null;

      const linksOf = (tab) => {
        const pane = content.querySelector(
          `.mega-menu__submenu[data-mega-panel="${tab.dataset.megaTab}"]`
        );
        return pane ? Array.from(pane.querySelectorAll("a[href]")) : [];
      };

      const tabIndex = tabs.indexOf(target);
      if (tabIndex !== -1) {
        if (!backwards) return linksOf(target)[0] || null;

        const prev = tabs[tabIndex - 1];
        const prevLinks = prev ? linksOf(prev) : [];
        if (!prevLinks.length) return null;
        activateTab(content, prev);
        return prevLinks[prevLinks.length - 1];
      }

      const pane = target.closest(".mega-menu__submenu");
      if (!pane) return null;
      const owner = tabs.find(
        (t) => t.dataset.megaTab === pane.dataset.megaPanel
      );
      if (!owner) return null;
      const links = linksOf(owner);

      if (backwards) return target === links[0] ? owner : null;
      if (target !== links[links.length - 1]) return null;
      return tabs[tabs.indexOf(owner) + 1] || null;
    };

    const onPanelTab = (event) => {
      if (event.key !== "Tab") return;
      if (!isDesktop() || !isPanelOpen() || cartTabHandler) return;

      const content = header.querySelector(
        "[data-panel-content].header-nav-panel__content--active"
      );
      const trigger = header.querySelector(
        "[data-panel-target].header-nav-trigger--active"
      );
      if (!content || !trigger) return;

      const target = event.target;

      if (trigger.contains(target)) {
        // Backwards out of the trigger leaves the panel behind — close it.
        if (event.shiftKey) {
          closePanel();
          return;
        }
        const entry = getFocusableElements(content)[0];
        if (!entry) return;
        event.preventDefault();
        entry.focus();
        return;
      }

      if (!content.contains(target)) return;

      const megaStep = megaTabStep(content, target, event.shiftKey);
      if (megaStep) {
        event.preventDefault();
        megaStep.focus();
        return;
      }

      const focusables = getFocusableElements(content);
      if (!focusables.length) return;

      if (event.shiftKey && target === focusables[0]) {
        event.preventDefault();
        firstFocusable(trigger)?.focus();
        return;
      }

      if (!event.shiftKey && target === focusables[focusables.length - 1]) {
        event.preventDefault();
        closePanel();
        focusAfterTrigger(trigger);
      }
    };

    if (header._panelTabHandler) {
      document.removeEventListener("keydown", header._panelTabHandler, true);
    }
    header._panelTabHandler = onPanelTab;
    document.addEventListener("keydown", onPanelTab, true);

    // Unbound menu items (plain links + native submenus) close the panel on hover.
    const unboundItems = header.querySelectorAll(
      ".header-inline-menu__item:not([data-panel-target])"
    );
    unboundItems.forEach((item) => {
      if (item.dataset.unboundInit === "true") return;
      item.dataset.unboundInit = "true";
      item.addEventListener("mouseenter", () => {
        if (isDesktop()) closePanel();
      });
    });

    // The panel's own backdrop closes the panel on click.
    if (backdrop && backdrop.dataset.backdropInit !== "true") {
      backdrop.dataset.backdropInit = "true";
      backdrop.addEventListener("click", closePanel);
    }

    // Mega tab switching inside each content block.
    contents.forEach((content) => {
      content.querySelectorAll(".mega-menu__tab").forEach((tab) => {
        if (tab.dataset.tabInit === "true") return;
        tab.dataset.tabInit = "true";
        const activate = () => activateTab(content, tab);
        tab.addEventListener("mouseenter", activate);
        tab.addEventListener("focus", activate);
        if (tab.tagName === "BUTTON") {
          tab.addEventListener("click", activate);
        }
      });
    });

    // Expose the shared API (names preserved for theme-editor.js + callers).
    const mega = (window.HeaderMegaModals = window.HeaderMegaModals || {});
    mega.closePanel = closePanel;
    mega.openPanel = openPanel;
    mega.closeMegaMenu = closePanel;
    mega.closeMegaSubmenu = closePanel;
    mega.closeMegaCards = closePanel;
    mega.closeSearchModal = closePanel;
    mega.openMegaMenu = (item) => openPanel(item?.dataset?.panelTarget, item);
    mega.openMegaSubmenu = (item) =>
      openPanel(item?.dataset?.panelTarget, item);
    mega.openMegaCards = (item) => openPanel(item?.dataset?.panelTarget, item);

    // Escape closes the open panel (bound once, globally).
    if (!document.body.dataset.panelEscBound) {
      document.body.dataset.panelEscBound = "true";
      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") window.HeaderMegaModals?.closePanel?.();
      });
    }

    // Leaving the desktop range must not leave an open (now CSS-hidden) panel and
    // its body scroll lock behind.
    if (!document.body.dataset.panelResizeBound) {
      document.body.dataset.panelResizeBound = "true";
      window.addEventListener("resize", () => {
        if (!isDesktop()) window.HeaderMegaModals?.closePanel?.();
      });
    }
  };

  // Tab-focusing a card in an off-screen slide makes the browser scroll the
  // nearest scrollable box to reveal it, which drags the whole search column —
  // trending list and slider controls included — sideways. Swiper's a11y module
  // slides the card in on its own, so pin the horizontal offset; the vertical
  // one still belongs to the modal/panel scroll.
  const pinSliderHostScroll = (element) => {
    if (!element || element.dataset.sliderScrollPinned === "true") return;
    element.dataset.sliderScrollPinned = "true";
    element.addEventListener("scroll", () => {
      if (element.scrollLeft !== 0) element.scrollLeft = 0;
    });
  };

  // Recommended-products slider(s) inside the search (desktop panel + mobile
  // modal). Lifecycle-safe: destroy any existing instance before re-creating.
  const initSearchRecommendedSlider = (header) => {
    const sliders = header.querySelectorAll(".js-search-recommended-slider");
    if (!sliders.length || typeof Swiper === "undefined") return;

    sliders.forEach((sliderEl) => {
      if (sliderEl.swiper) {
        sliderEl.swiper.destroy(true, true);
        sliderEl.swiper = null;
      }
      const scope =
        sliderEl.closest(".header-search-modal__recommended") ||
        sliderEl.closest(".header-search-panel__recommended") ||
        sliderEl.parentElement;
      const paginationEl = scope?.querySelector(
        ".js-search-recommended-pagination"
      );
      const nextBtn = scope?.querySelector(".slider-nav-button--next");
      const prevBtn = scope?.querySelector(".slider-nav-button--prev");

      [
        sliderEl,
        sliderEl.closest(".header-search-modal__inner"),
        sliderEl.closest(".header-search-modal__body"),
        sliderEl.closest(".header-nav-panel__scroll"),
      ].forEach(pinSliderHostScroll);

      new Swiper(sliderEl, {
        slidesPerView: 1,
        spaceBetween: 12,
        speed: 600,
        // spelled `watchSlidesProgress`: it feeds Swiper's `visibleSlides`, which
        // the a11y module checks before sliding on focus — without it every Tab
        // into an already-visible card jumps the slider
        watchSlidesProgress: true,
        mousewheel: {
          forceToAxis: true,
        },
        breakpoints: {
          576: { slidesPerView: 2, spaceBetween: 16 },
          1200: { slidesPerView: 3, spaceBetween: 16 },
        },
        pagination: paginationEl
          ? { el: paginationEl, type: "progressbar" }
          : false,
        navigation:
          nextBtn && prevBtn
            ? {
                nextEl: nextBtn,
                prevEl: prevBtn,
                disabledClass: "swiper-button-disabled",
              }
            : false,
      });
    });
  };

  // Placeholder SVGs ship a clip-path that can hide them inside the header
  // chrome — strip it whenever the header renders placeholder media.
  const stripPlaceholderClipPaths = (header) => {
    if (!header.querySelector("svg.placeholder-svg")) return;
    document
      .querySelectorAll("svg.placeholder-svg g[clip-path]")
      .forEach((el) => {
        el.removeAttribute("clip-path");
      });
  };

  // <shopify-account> renders its sheet in an open shadow root, so the menu-row
  // hover can only be restyled from inside: the fill that swallowed the row
  // separators is dropped and the row dims its own text instead.
  // The doubled class only buys specificity over the component's own rules.
  const ACCOUNT_HOVER_CSS = `
    .account__item.account__item {
      transition: color var(--duration-default, 0.3s) ease-in-out !important;
    }

    @media (hover: hover) {
      .account__item.account__item:hover {
        background: none !important;
        color: rgba(var(--color-foreground), 0.7) !important;
      }
    }

    .account__item.account__item:focus-visible {
      background: none !important;
    }
  `;

  const styleAccountShadow = (element) => {
    const root = element.shadowRoot;
    if (!root) return false;
    if (root.themeAccountHoverStyled) return true;

    const style = document.createElement("style");
    style.textContent = ACCOUNT_HOVER_CSS;
    root.append(style);
    root.themeAccountHoverStyled = true;
    return true;
  };

  const initAccountShadowStyles = (header) => {
    const element = header.querySelector("shopify-account");
    if (!element) return;

    clearTimeout(element._accountStyleTimer);

    // The shadow root only exists once the component upgrades, which happens
    // after Shopify's own script arrives.
    const attempt = (tries) => {
      if (styleAccountShadow(element)) return;
      if (tries >= 100) return;
      element._accountStyleTimer = setTimeout(() => attempt(tries + 1), 100);
    };

    attempt(0);
  };

  // Links announced as buttons (search, cart) must answer Space like one; Enter
  // already clicks a link natively.
  const initLinkButtons = (header) => {
    header.querySelectorAll('a[role="button"]').forEach((link) => {
      if (link.dataset.spaceInit === "true") return;
      link.dataset.spaceInit = "true";
      link.addEventListener("keydown", (event) => {
        if (event.key !== " ") return;
        event.preventDefault();
        link.click();
      });
    });
  };

  // A row whose label is a link toggles from its caret button (see
  // snippets/submenu-caret.liquid). Pointer clicks pass through the caret to the
  // summary, so this only answers keyboard activation.
  const initSubmenuToggles = (header) => {
    header.querySelectorAll(".js-submenu-toggle").forEach((toggle) => {
      if (toggle.dataset.toggleInit === "true") return;
      toggle.dataset.toggleInit = "true";
      const details = toggle.closest("details");
      if (!details) return;

      toggle.addEventListener("click", (event) => {
        event.preventDefault();
        details.open = !details.open;
      });
      details.addEventListener("toggle", () => {
        toggle.setAttribute("aria-expanded", String(details.open));
      });
    });
  };

  const initHeader = (event) => {
    const header = document.querySelector(".shopify-section-header");
    if (!header) return;

    if (isHeaderSectionEvent(event)) {
      resetHeaderModalState();
    }

    initHeaderOverlay(header);
    initDesktopPanel(header);
    initDesktopSubmenu(header);
    initMenuGroupWidth(header);
    initDropdownOffset(header);
    initCentredLogoWidth(header);
    initMobileMegaSubmenu(header);
    initLinkButtons(header);
    initSubmenuToggles(header);
    initSearchRecommendedSlider(header);
    initAccountShadowStyles(header);
    stripPlaceholderClipPaths(header);
  };

  const teardownHeader = (event) => {
    const root = event && event.target;
    const sliders =
      (root && root.querySelectorAll
        ? root.querySelectorAll(".js-search-recommended-slider")
        : null) || document.querySelectorAll(".js-search-recommended-slider");
    sliders.forEach((slider) => {
      if (slider.swiper) {
        slider.swiper.destroy(true, true);
        slider.swiper = null;
      }
    });

    if (root && root._submenuObserver) {
      root._submenuObserver.disconnect();
      root._submenuObserver = null;
    }

    if (root && root._menuGroupObserver) {
      root._menuGroupObserver.disconnect();
      root._menuGroupObserver = null;
    }

    const account =
      root && root.querySelector ? root.querySelector("shopify-account") : null;
    if (account) {
      clearTimeout(account._accountStyleTimer);
      account._accountStyleTimer = null;
    }

    if (root && root.querySelector && root.querySelector(".header-wrapper")) {
      resetHeaderModalState();
    }
  };

  initHeader();

  document.addEventListener("shopify:section:load", initHeader);
  document.addEventListener("shopify:section:unload", teardownHeader);
  document.addEventListener("shopify:section:reorder", initHeader);
})();

// Mobile search modal (< 1200px). On desktop the search content lives in the
// navigation panel (handled above), so this element bails out.
const SEARCH_DESKTOP_MIN = 1200; // $xl — the panel breakpoint above
if (!customElements.get("header-search")) {
  customElements.define(
    "header-search",
    class HeaderSearch extends HTMLElement {
      constructor() {
        super();
        this.open = this.onOpen.bind(this);
        this.close = this.onClose.bind(this);
        this.onKeyDownBody = this.onKeydownBody.bind(this);
        this.onKeyDownOption = this.onKeydownOption.bind(this);
        this.onKeyUpOption = this.onKeyupOption.bind(this);
      }

      connectedCallback() {
        this.header = document.querySelector(".header-wrapper");
        this.openBtn = this.querySelector(".js-search-modal-trigger");
        this.modalEl = document.querySelector(".header-search-modal");
        if (!this.modalEl) return;

        this.closeBtn = this.modalEl.querySelector(".js-search-close-trigger");
        this.backdropEl = this.modalEl.querySelector(
          ".header-search-modal__backdrop"
        );
        this.input = this.modalEl.querySelector("#Search-In-Modal");
        this.menuEl = this.modalEl.querySelector(".header-search-modal__menu");
        this.predictiveSearchEl =
          this.modalEl.querySelector("predictive-search");

        this.isMac = this.identifyDevice();

        this.openBtn?.addEventListener("click", this.open);
        this.closeBtn?.addEventListener("click", this.close);
        this.backdropEl?.addEventListener("click", this.close);

        document.body.addEventListener("keydown", this.onKeyDownBody);
        this.modalEl.addEventListener("keydown", this.onKeyDownOption);
        this.modalEl.addEventListener("keyup", this.onKeyUpOption);
      }

      disconnectedCallback() {
        document.body.removeEventListener("keydown", this.onKeyDownBody);
      }

      onOpen(event) {
        // Desktop uses the navigation panel — never open the mobile modal there.
        if (window.innerWidth >= SEARCH_DESKTOP_MIN) return;
        if (event) event.preventDefault();
        if (this.isOpen) return;
        if (this.header) this.header.preventHide = true;

        this.isOpen = true;
        this.modalEl.setAttribute("open", true);
        if (this.openBtn) this.openBtn.setAttribute("aria-expanded", "true");

        this.modalEl.addEventListener(
          "transitionend",
          () => {
            if (this.isOpen) trapFocus(this.modalEl);
          },
          { once: true }
        );

        this._focusTimeout = setTimeout(() => {
          if (this.input) this.input.focus();
        }, 400);

        document.body.classList.add("overflow-hidden-search-open");

        if (typeof window.lockScrollBodyOnIos === "function") {
          this.openScrollY = window.scrollY;
          window.lockScrollBodyOnIos(this.openScrollY);
        }
      }

      onClose(event, elementToFocus = null) {
        if (event) event.preventDefault();
        if (!this.isOpen) return;
        clearTimeout(this._focusTimeout);
        this.isOpen = false;
        this.modalEl.removeAttribute("open");
        if (this.openBtn) this.openBtn.setAttribute("aria-expanded", "false");
        if (this.header) this.header.preventHide = false;

        document.body.classList.remove("overflow-hidden-search-open");

        if (this.input) this.input.setAttribute("aria-activedescendant", "");

        const newFocusElement = elementToFocus ? elementToFocus : this.openBtn;
        removeTrapFocus(newFocusElement);

        if (typeof window.unlockScrollBodyOnIos === "function") {
          window.unlockScrollBodyOnIos(this.openScrollY);
        }
      }

      onKeyupOption(event) {
        const isOpen = this.modalEl.getAttribute("open");
        if (!isOpen || !(this.menuEl || this.predictiveSearchEl)) return;
        if (event.code === "Enter") {
          const target = event.target;
          if (target.tagName.toLowerCase() === "button") return;
          event.preventDefault();
          this.selectOption();
        }
      }

      onKeydownOption(event) {
        const isOpen = this.modalEl.getAttribute("open");
        if (!isOpen || !(this.menuEl || this.predictiveSearchEl)) return;
        if (event.code === "ArrowUp" || event.code === "ArrowDown") {
          const targetTag = event.target.tagName.toLowerCase();
          if (targetTag !== "input" && targetTag !== "a" && targetTag !== "div")
            return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.switchOption(event.code === "ArrowUp" ? "up" : "down");
        }
      }

      onKeydownBody(event) {
        const isOpen = this.modalEl.getAttribute("open");
        if (isOpen && event.key === "Escape") {
          this.close();
          return;
        }
        if (!isOpen) {
          const key = event?.key?.toLowerCase();
          const isMac = this.isMac;
          const isCmdK = isMac && event?.metaKey && key === "k";
          const isCtrlK = !isMac && event?.ctrlKey && key === "k";
          if (isCmdK || isCtrlK) {
            // Cmd/Ctrl+K opens desktop panel search or the mobile modal.
            if (window.innerWidth >= SEARCH_DESKTOP_MIN) {
              event.preventDefault();
              window.HeaderMegaModals?.openPanel?.(
                "panel-search",
                this.openBtn,
                { focusSearch: true }
              );
            } else {
              event.preventDefault();
              this.open();
            }
          }
        }
      }

      switchOption(direction) {
        if (!this.modalEl.getAttribute("open")) return;
        const moveUp = direction === "up";
        const selectedElement = this.modalEl.querySelector(
          '[aria-selected="true"]'
        );
        const allVisibleElements = Array.from(
          this.modalEl.querySelectorAll("[role='option']")
        ).filter((element) => element.offsetParent !== null);
        if (allVisibleElements.length === 0) return;

        let selectedElementIndex = allVisibleElements.indexOf(selectedElement);
        let activeElementIndex = 0;
        if (selectedElementIndex === -1) {
          activeElementIndex = moveUp ? allVisibleElements.length - 1 : 0;
        } else if (moveUp) {
          activeElementIndex =
            selectedElementIndex === 0
              ? allVisibleElements.length - 1
              : selectedElementIndex - 1;
        } else {
          activeElementIndex =
            selectedElementIndex === allVisibleElements.length - 1
              ? 0
              : selectedElementIndex + 1;
        }

        if (this.predictiveSearchEl && this.predictiveSearchEl.statusElement) {
          this.predictiveSearchEl.statusElement.textContent = "";
        }

        const activeElement = allVisibleElements[activeElementIndex];
        if (!activeElement) return;

        activeElement.setAttribute("aria-selected", true);
        if (selectedElement)
          selectedElement.setAttribute("aria-selected", false);
        if (this.input)
          this.input.setAttribute("aria-activedescendant", activeElement.id);

        activeElement.scrollIntoView({
          block: "nearest",
          inline: "nearest",
          behavior: "smooth",
        });
      }

      selectOption() {
        const selectedOption = this.modalEl.querySelector(
          '[aria-selected="true"] a, a[aria-selected="true"]'
        );
        if (selectedOption) selectedOption.click();
      }

      identifyDevice() {
        return (
          /Mac/.test(navigator.platform) ||
          navigator.userAgentData?.platform?.toLowerCase() === "macos" ||
          navigator.userAgentData?.platform?.toLowerCase().includes("mac")
        );
      }
    }
  );
}

if (!customElements.get("header-dropdown")) {
  customElements.define(
    "header-dropdown",
    class HeaderDropdown extends HTMLElement {
      constructor() {
        super();
        this._onOpen = this.openDropdown.bind(this);
        this._onClose = this.closeDropdown.bind(this);
        this._onOutsideClick = this.onOutsideClick.bind(this);
        this._onKeyDown = this.onKeyDown.bind(this);
      }

      connectedCallback() {
        this.openBtn = this.querySelector(".js-dropdown-trigger");
        this.closeBtn = this.querySelector(".js-dropdown-close-trigger");
        this.modalEl = this.querySelector(".header-dropdown__modal");

        this.openBtn?.addEventListener("click", this._onOpen);
        this.closeBtn?.addEventListener("click", this._onClose);
        this.addEventListener("keydown", this._onKeyDown);
        document.addEventListener("click", this._onOutsideClick);
      }

      disconnectedCallback() {
        this.openBtn?.removeEventListener("click", this._onOpen);
        this.closeBtn?.removeEventListener("click", this._onClose);
        this.removeEventListener("keydown", this._onKeyDown);
        document.removeEventListener("click", this._onOutsideClick);
        clearTimeout(this._trapTimer);
      }

      onKeyDown(event) {
        if (event.key !== "Escape" || !this.getAttribute("open")) return;
        event.stopPropagation();
        this.closeDropdown();
      }

      openDropdown() {
        if (!this.getAttribute("open")) {
          // The dropdown and the desktop navigation panel (mega menu / search /
          // cart) are mutually exclusive — the panel would otherwise stay open
          // behind it, backdrop included.
          window.HeaderMegaModals?.closePanel?.();
          this.setAttribute("open", "true");
          this.openBtn?.setAttribute("aria-expanded", "true");

          // transitionend can fail to fire (reduced motion, no transition);
          // the timer makes sure focus still enters, once
          let focusEntered = false;
          const enterFocus = () => {
            if (focusEntered) return;
            focusEntered = true;
            clearTimeout(this._trapTimer);
            // a panel opening (or an outside click) can close us mid-fade —
            // trapping focus then would lock it inside the hidden dropdown
            if (this.getAttribute("open")) trapFocus(this.modalEl);
          };
          this.addEventListener("transitionend", enterFocus, { once: true });
          clearTimeout(this._trapTimer);
          this._trapTimer = setTimeout(enterFocus, 350);
        } else {
          this.closeDropdown();
        }
      }

      closeDropdown() {
        clearTimeout(this._trapTimer);
        this.removeAttribute("open");
        this.openBtn?.setAttribute("aria-expanded", "false");
        removeTrapFocus(this.openBtn);
      }

      // Closed because the desktop navigation panel took over: release the trap,
      // but leave focus where the new owner put it instead of pulling it back.
      closeForHandoff() {
        if (!this.getAttribute("open")) return;
        clearTimeout(this._trapTimer);
        this.removeAttribute("open");
        this.openBtn?.setAttribute("aria-expanded", "false");
        removeTrapFocus();
      }

      onOutsideClick(event) {
        if (
          !this.getAttribute("open") ||
          this.contains(event.target) ||
          this.openBtn.contains(event.target)
        ) {
          return;
        }
        this.closeDropdown();
      }
    }
  );
}
